import React, { useState, useEffect } from 'react';

function Settings() {
    const [settings, setSettings] = useState({
        login_logo: null,
        dashboard_logo: null
    });
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const token = localStorage.getItem('token');

            const logoResponse = await fetch('/api/settings/logos', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const logoData = await logoResponse.json();

            setSettings({
                login_logo: logoData.login_logo,
                dashboard_logo: logoData.dashboard_logo
            });
            setLoading(false);
        } catch (error) {
            console.error('Error fetching settings:', error);
            setLoading(false);
        }
    };

    const handleLogoUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('logo', file);
        formData.append('type', type);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/settings/upload-logo', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                alert(`${type === 'login' ? 'Login' : 'Dashboard'} logo uploaded successfully!`);
                fetchSettings();
            }
        } catch (error) {
            console.error('Error uploading logo:', error);
            alert('Failed to upload logo');
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
                color: '#666'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', marginBottom: '12px' }}>⚙️</div>
                    <p>Loading settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fade-in-up">
            <div style={{ marginBottom: '32px' }}>
                <h1 style={{
                    fontSize: '36px',
                    fontWeight: '700',
                    marginBottom: '8px',
                    color: '#1a1a1a'
                }}>Settings</h1>
                <p style={{
                    fontSize: '16px',
                    color: '#666666',
                    margin: 0
                }}>Manage your brand logos and visual identity.</p>
            </div>

            <div className="glass-card" style={{ marginBottom: '24px' }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '24px'
                }}>
                    <span style={{ fontSize: '24px' }}>🎨</span>
                    <div>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '700',
                            margin: 0,
                            color: '#1a1a1a'
                        }}>Brand Logos</h2>
                        <p style={{
                            fontSize: '14px',
                            color: '#666',
                            margin: '4px 0 0 0'
                        }}>Recommended size: 500px × 124px</p>
                    </div>
                </div>

                <div style={{ marginBottom: '32px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '12px',
                        fontWeight: '600',
                        color: '#1a1a1a',
                        fontSize: '14px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Login Page Logo
                    </label>

                    <div style={{
                        position: 'relative',
                        border: '2px dashed #e2e8f0',
                        borderRadius: '12px',
                        padding: '24px',
                        background: 'rgba(255, 255, 255, 0.3)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        textAlign: 'center'
                    }}>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleLogoUpload(e, 'login')}
                            disabled={uploading}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                opacity: 0,
                                cursor: uploading ? 'not-allowed' : 'pointer'
                            }}
                        />
                        <div style={{ pointerEvents: 'none' }}>
                            <div style={{
                                fontSize: '32px',
                                marginBottom: '8px'
                            }}>📤</div>
                            <p style={{
                                color: '#1a1a1a',
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '0 0 4px 0'
                            }}>
                                Click to upload logo
                            </p>
                            <p style={{
                                color: '#666',
                                fontSize: '12px',
                                margin: 0
                            }}>
                                PNG, JPG up to 5MB
                            </p>
                        </div>
                    </div>

                    {settings.login_logo && (
                        <div style={{
                            marginTop: '16px',
                            padding: '16px',
                            background: 'rgba(255, 255, 255, 0.3)',
                            borderRadius: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}>
                            <p style={{
                                fontSize: '12px',
                                color: '#666',
                                marginBottom: '8px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Current Logo:
                            </p>
                            <img
                                src={`${settings.login_logo}`}
                                alt="Login Logo"
                                style={{
                                    maxHeight: '62px',
                                    display: 'block',
                                    borderRadius: '8px'
                                }}
                            />
                        </div>
                    )}
                </div>

                <div style={{ marginBottom: uploading ? '24px' : '0' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '12px',
                        fontWeight: '600',
                        color: '#1a1a1a',
                        fontSize: '14px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Dashboard Logo
                    </label>

                    <div style={{
                        position: 'relative',
                        border: '2px dashed #e2e8f0',
                        borderRadius: '12px',
                        padding: '24px',
                        background: 'rgba(255, 255, 255, 0.3)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        textAlign: 'center'
                    }}>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleLogoUpload(e, 'dashboard')}
                            disabled={uploading}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                opacity: 0,
                                cursor: uploading ? 'not-allowed' : 'pointer'
                            }}
                        />
                        <div style={{ pointerEvents: 'none' }}>
                            <div style={{
                                fontSize: '32px',
                                marginBottom: '8px'
                            }}>📤</div>
                            <p style={{
                                color: '#1a1a1a',
                                fontSize: '14px',
                                fontWeight: '600',
                                margin: '0 0 4px 0'
                            }}>
                                Click to upload logo
                            </p>
                            <p style={{
                                color: '#666',
                                fontSize: '12px',
                                margin: 0
                            }}>
                                PNG, JPG up to 5MB
                            </p>
                        </div>
                    </div>

                    {settings.dashboard_logo && (
                        <div style={{
                            marginTop: '16px',
                            padding: '16px',
                            background: 'rgba(255, 255, 255, 0.3)',
                            borderRadius: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}>
                            <p style={{
                                fontSize: '12px',
                                color: '#666',
                                marginBottom: '8px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Current Logo:
                            </p>
                            <img
                                src={`${settings.dashboard_logo}`}
                                alt="Dashboard Logo"
                                style={{
                                    maxHeight: '62px',
                                    display: 'block',
                                    borderRadius: '8px'
                                }}
                            />
                        </div>
                    )}
                </div>

                {uploading && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        background: 'rgba(112, 0, 0, 0.1)',
                        borderRadius: '12px',
                        border: '1px solid rgba(112, 0, 0, 0.2)',
                        marginTop: '24px'
                    }}>
                        <div style={{
                            width: '20px',
                            height: '20px',
                            border: '3px solid #700000',
                            borderTopColor: 'transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }} />
                        <span style={{
                            color: '#700000',
                            fontWeight: '600',
                            fontSize: '14px'
                        }}>
                            Uploading logo...
                        </span>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    to {
                        transform: rotate(360deg);
                    }
                }
            `}</style>
        </div>
    );
}

export default Settings;